/**
 * CandidatePortal.js
 *
 * Public page — no login required.
 * Candidates enter their email + application ID to check their status.
 *
 * Route: /candidate-portal  (add to App.js as a public route)
 * API:   POST /ats/public/portal/login
 *
 * The application ID is sent to the candidate in their application_received
 * email. Candidates visit this page from that email link.
 */
import React, { useState } from 'react';
import { Card, Form, Button, Spinner, Alert, Badge, Row, Col } from 'react-bootstrap';
import { Search, CheckCircle, Clock, XCircle, Mail, Briefcase, MapPin, Calendar } from 'lucide-react';
import axiosInstance from '../../api/axiosConfig';

// Pipeline stage order for progress bar
const STAGE_ORDER = [
    'Applied', 'Screening', 'Shortlisted', 'Interview',
    'Technical', 'HR Round', 'Offer', 'Hired',
];

const STAGE_ICONS = {
    Applied:     '📋',
    Screening:   '🔍',
    Shortlisted: '⭐',
    Interview:   '🗣️',
    Technical:   '💻',
    'HR Round':  '👥',
    Offer:       '🎁',
    Hired:       '🎉',
    Rejected:    '❌',
};

const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : '—';

const CandidatePortal = () => {
    const [email,         setEmail]         = useState('');
    const [applicationId, setApplicationId] = useState('');
    const [loading,       setLoading]       = useState(false);
    const [result,        setResult]        = useState(null);
    const [error,         setError]         = useState('');
    const [touched,       setTouched]       = useState(false);

    const validate = () => {
        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
            return 'Please enter a valid email address';
        if (!applicationId.trim() || isNaN(parseInt(applicationId)))
            return 'Please enter a valid Application ID (number)';
        return '';
    };

    const handleCheck = async (e) => {
        e.preventDefault();
        setTouched(true);
        const validErr = validate();
        if (validErr) { setError(validErr); return; }
        setLoading(true);
        setError('');
        setResult(null);
        try {
            const res = await axiosInstance.post('/ats/public/portal/login', {
                email:          email.trim().toLowerCase(),
                application_id: parseInt(applicationId),
            });
            setResult(res.data.data);
        } catch (err) {
            const msg = err.response?.data?.message || 'Could not find your application. Please check your details.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setResult(null);
        setError('');
        setTouched(false);
    };

    // Progress bar — how far through the pipeline
    const stageIndex   = result ? STAGE_ORDER.indexOf(result.stage) : -1;
    const isRejected   = result?.stage === 'Rejected';
    const isHired      = result?.stage === 'Hired';
    const progressPct  = isRejected ? 0
                       : stageIndex === -1 ? 0
                       : Math.round(((stageIndex + 1) / STAGE_ORDER.length) * 100);

    return (
        <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #eef2ff 0%, #faf5ff 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
            <div style={{ width: '100%', maxWidth: 540 }}>
                {/* Header */}
                <div className="text-center mb-4">
                    <div className="text-center mb-4">
                        <img src="/logo.png" alt="PeoplesDock" style={{ height: 70 }}/>
                    </div>
                </div>

                {/* Lookup form */}
                {!result && (
                    <>
                        <div className="text-center mb-4">
                        <div className="text-center mb-4">
                            <h3 className="fw-bold mb-1" style={{ color: '#1e293b' }}>Track Your Application</h3>
                            <p className="text-muted small">Enter your email and Application ID to check your status</p>
                            </div>
                        </div>
                        <Card className="border-0 shadow" style={{ borderRadius: 16 }}>
                            <Card.Body className="p-4">
                                <Form onSubmit={handleCheck}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-bold">Email Address</Form.Label>
                                        <Form.Control
                                            type="email"
                                            placeholder="you@example.com"
                                            value={email}
                                            onChange={e => { setEmail(e.target.value); setError(''); }}
                                            isInvalid={touched && !email.trim()}
                                            style={{ borderRadius: 10 }}
                                            autoFocus
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            Please enter your email address
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group className="mb-4">
                                        <Form.Label className="small fw-bold">Application ID</Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="e.g. 42"
                                            value={applicationId}
                                            onChange={e => { setApplicationId(e.target.value); setError(''); }}
                                            isInvalid={touched && !applicationId.trim()}
                                            style={{ borderRadius: 10 }}
                                            min="1"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            Please enter your Application ID
                                        </Form.Control.Feedback>
                                        <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                                            Your Application ID was included in your application confirmation email.
                                        </Form.Text>
                                    </Form.Group>
                                    {error && (
                                        <Alert variant="danger" className="small py-2 mb-3">
                                            {error}
                                        </Alert>
                                    )}
                                    <Button
                                        type="submit"
                                        className="w-100 fw-bold"
                                        disabled={loading}
                                        style={{ borderRadius: 10, background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', border: 'none', padding: '12px' }}>
                                        {loading
                                            ? <><Spinner size="sm" className="me-2" />Checking…</>
                                            : <><Search size={15} className="me-2" />Check My Status</>
                                        }
                                    </Button>
                                </Form>
                            </Card.Body>
                        </Card>
                    </>
                )}

                {/* Result card */}
                {result && (
                    <Card className="border-0 shadow" style={{ borderRadius: 16 }}>
                        <Card.Body className="p-0">
                            {/* Top colour band */}
                            <div style={{ background: isRejected ? '#dc2626' : isHired ? '#059669' : '#4f46e5', borderRadius: '16px 16px 0 0', padding: '24px', color: '#fff' }}>
                                <div className="d-flex align-items-center gap-3 mb-2">
                                    <div style={{ fontSize: 32 }}>{STAGE_ICONS[result.stage] || '📋'}</div>
                                    <div>
                                        <div className="fw-bold" style={{ fontSize: 18 }}>{result.stage}</div>
                                        <div style={{ opacity: 0.82, fontSize: 13 }}>{result.job_title}</div>
                                    </div>
                                    <Badge style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: 11 }}>
                                        {result.company_name}
                                    </Badge>
                                </div>
                                <p style={{ margin: 0, fontSize: 14, opacity: 0.9, lineHeight: 1.6 }}>
                                    {result.stage_message}
                                </p>
                            </div>

                            <div className="p-4">
                                {/* Progress bar (only for active) */}
                                {!isRejected && (
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between small text-muted mb-1">
                                            <span>Progress</span>
                                            <span>{progressPct}%</span>
                                        </div>
                                        <div style={{ height: 8, background: '#f1f5f9', borderRadius: 8 }}>
                                            <div style={{ height: '100%', borderRadius: 8, width: `${progressPct}%`, background: isHired ? '#059669' : 'linear-gradient(90deg,#4f46e5,#7c3aed)', transition: 'width 0.5s ease' }} />
                                        </div>
                                        <div className="d-flex justify-content-between mt-2" style={{ fontSize: 10 }}>
                                            <span style={{ color: '#94a3b8' }}>Applied</span>
                                            <span style={{ color: '#059669' }}>Hired</span>
                                        </div>
                                    </div>
                                )}

                                {/* Details grid */}
                                <Row className="g-3 mb-4">
                                    <Col xs={6}>
                                        <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Candidate</div>
                                        <div className="fw-semibold" style={{ fontSize: 14 }}>{result.candidate_name}</div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Application ID</div>
                                        <div className="fw-semibold" style={{ fontSize: 14 }}>#{result.application_id}</div>
                                    </Col>
                                    {result.location && (
                                        <Col xs={6}>
                                            <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Location</div>
                                            <div style={{ fontSize: 14 }}>
                                                <MapPin size={12} className="me-1 text-muted" />{result.location}
                                            </div>
                                        </Col>
                                    )}
                                    {result.employment_type && (
                                        <Col xs={6}>
                                            <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Type</div>
                                            <div style={{ fontSize: 14 }}>
                                                <Briefcase size={12} className="me-1 text-muted" />{result.employment_type}
                                            </div>
                                        </Col>
                                    )}
                                    <Col xs={6}>
                                        <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Applied On</div>
                                        <div style={{ fontSize: 14 }}>
                                            <Calendar size={12} className="me-1 text-muted" />{fmtDate(result.applied_at)}
                                        </div>
                                    </Col>
                                    <Col xs={6}>
                                        <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Last Updated</div>
                                        <div style={{ fontSize: 14 }}>
                                            <Clock size={12} className="me-1 text-muted" />{fmtDate(result.last_updated)}
                                        </div>
                                    </Col>
                                </Row>

                                {/* Stage pills */}
                                {!isRejected && (
                                    <div className="mb-4">
                                        <div className="text-muted small fw-bold mb-2" style={{ textTransform: 'uppercase', fontSize: 11 }}>Pipeline</div>
                                        <div className="d-flex flex-wrap gap-1">
                                            {STAGE_ORDER.map((s, i) => {
                                                const isDone    = i < stageIndex;
                                                const isCurrent = i === stageIndex;
                                                return (
                                                    <span key={s} style={{
                                                        fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: isCurrent ? 700 : 400,
                                                        background: isCurrent ? '#4f46e5' : isDone ? '#d1fae5' : '#f1f5f9',
                                                        color: isCurrent ? '#fff' : isDone ? '#065f46' : '#94a3b8',
                                                        border: isCurrent ? '2px solid #4f46e5' : '2px solid transparent',
                                                    }}>
                                                        {isDone ? '✓ ' : ''}{s}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                <div className="d-flex gap-2">
                                    <Button variant="outline-secondary" size="sm" onClick={reset} style={{ borderRadius: 8 }}>
                                        ← Check Another
                                    </Button>
                                    <Button variant="outline-primary" size="sm"
                                        href={`mailto:hr@${result.company_name?.toLowerCase().replace(/\s/g, '')}.com`}
                                        style={{ borderRadius: 8 }}>
                                        <Mail size={12} className="me-1" />Contact HR
                                    </Button>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                )}

                <p className="text-center text-muted mt-4" style={{ fontSize: 12 }}>
                    Powered by <strong>PeoplesDock</strong> · Applicant Tracking System
                </p>
            </div>
        </div>
    );
};

export default CandidatePortal;
